// src/connectors/api/connectorsApi.js
//
// Real connectors API — calls architecture_scaffold's
// presentation/shared/connections/connections_controller.py endpoints via
// src/api/client.js's apiGet/apiPost helpers. Covers Source Control and
// Work Item only — LLM connections have their own api module
// (src/llmConnections/api/llmConnectionsApi.js), since they support a
// different registration shape (org/account/project/personal levels).
//
// The backend has no `deactivate` route for either family and no `crm`
// family at all (confirmed against the controller) — deactivateConnection
// isn't exported here, and listConnections("crm") short-circuits to an
// empty list with no network call. ConnectorsPage/ConnectionCard disable
// the corresponding UI actions rather than calling something that always
// fails.
//
// The backend doesn't persist a connection's last-test result (test is a
// read-only probe, `ConnectionTestResponse`, never written back to the
// connection record) — so a fresh list() only ever carries `is_active`.
// ConnectorsPage keeps the most recent test result in local, session-only
// state and merges it in for display; it's honest to lose that on reload
// rather than inventing a persisted status the backend doesn't have.

import { apiGet, apiPost } from "../../api/client";

const FAMILY_PATH = {
  source_control: "source-control",
  work_item: "work-items",
};

function toConnection(dto) {
  return {
    id: dto.connection_id,
    name: dto.name,
    provider: dto.provider,
    status: dto.is_active ? "active" : "untested",
    lastTestedAt: null,
    lastError: undefined,
  };
}

function buildRegisterPayload(family, provider, values) {
  const { name } = values;

  if (family === "source_control") {
    if (provider === "github") {
      return { name, provider, pat: values.token, config: { repository: values.repository } };
    }
    if (provider === "azure_devops") {
      return {
        name,
        provider,
        pat: values.pat,
        config: { org: values.org, project: values.project, repository: values.repository },
      };
    }
    if (provider === "bitbucket") {
      return { name, provider, pat: values.pat, config: { repository: values.repository } };
    }
    // servicenow — its tester is a permanent stub on the backend (not a
    // conventional repo/API), so this just satisfies the request schema.
    return {
      name,
      provider,
      pat: values.password,
      config: { instance_url: values.instanceUrl, username: values.username },
    };
  }

  // work_item
  if (provider === "jira") {
    return {
      name,
      provider,
      pat: values.apiToken,
      config: {
        jira_base_url: values.siteUrl,
        jira_email: values.email,
        jira_project_key: values.projectKey,
      },
    };
  }
  // azure_devops (work item)
  return {
    name,
    provider,
    pat: values.pat,
    config: { org: values.org, project: values.project },
  };
}

export async function listConnections(family) {
  if (family === "crm") return [];
  const dtos = await apiGet(`/v1/connections/${FAMILY_PATH[family]}`);
  return (dtos || []).map(toConnection);
}

export async function registerConnection(family, payload) {
  const body = buildRegisterPayload(family, payload.provider, payload);
  const dto = await apiPost(`/v1/connections/${FAMILY_PATH[family]}`, body);
  const testResult = await testConnectionRaw(family, dto.connection_id);
  return {
    connection: toConnection(dto),
    testResult: { status: testResult.success ? "passed" : "failed", message: testResult.message },
  };
}

async function testConnectionRaw(family, id) {
  return apiPost(`/v1/connections/${FAMILY_PATH[family]}/${id}/test`);
}

export async function testConnection(family, id) {
  const result = await testConnectionRaw(family, id);
  return { status: result.success ? "passed" : "failed", message: result.message };
}
